import { Autocomplete, Button, TextField } from "@mui/material";
import { memo } from "react";

import LoadingIndicator from "../../../global/LoadingIndicator";
import Modal from "../../../global/Modal";
import ResponseModal from "../../../global/ResponseModal";
import DragAndDropContainer from "../../../ui library/components/file-upload/DragAndDropContainer";
import CustomInputLabel from "../../Common/CustomInputLabel";

import Header from "./components/Header";
import useDocumentState from "./hooks/useDocumentState";
import { filterOptionsByLabel } from "./utils/functions";
import {
  clearButtonStyles,
  searchableFields,
  textFieldStyles,
  uploadButtonStyles,
} from "./utils/strings";

const EditUploadDocument = ({
  disableForm = false,
  externalOnSubmit,
  externalRefresh,
  initialSelectedDocument = null,
  isFormModule = false,
  onClose,
  open,
  prefillOverrides = null,
  selectedDocument,
  shouldHydrateSelectedDocument = false,
  title = null,
}) => {
  const {
    allowSave,
    closeModal,
    errorArray,
    fieldInputValues,
    formData,
    handleChange,
    handleFieldSearchChange,
    handleFileChange,
    handleSubmit,
    handleUploadSuccess,
    hasChanged,
    initialFormData,
    isLoading,
    options,
    requiredFields,
    resetForm,
    setUploadFailed,
    setUploadSuccess,
    supportedFiles,
    uploadFailed,
    uploadSuccess,
  } = useDocumentState({
    externalOnSubmit,
    externalRefresh,
    initialSelectedDocument,
    isFormModule,
    onClose,
    open,
    prefillOverrides,
    selectedDocument,
    shouldHydrateSelectedDocument,
  });

  return (
    <Modal open={open} onClose={closeModal} maxWidth="md">
      <div className="h-full p-2">
        <Header
          isEditing={!!selectedDocument}
          maxDocSize={options.maxFileSize}
          supportedFiles={supportedFiles}
          title={title}
        />

        {/* Fields */}
        <div className="mt-8 grid grid-cols-3 gap-4">
          <div className="w-full" data-testid="container-docName">
            <CustomInputLabel text="Document Name" />
            <TextField
              fullWidth
              disabled
              size="small"
              sx={textFieldStyles}
              value={formData?.fileName || ""}
            />
          </div>

          {searchableFields.map(({ label, key, optsKey, enableSearchQuery }) => {
            const baseOpts = (options[optsKey] || []).map((option, idx) => ({
              ...option,
              optionKey: `option-${key}-${idx}`,
            }));
            const isRequired = !!requiredFields[key];
            const notReqOpt = {
              id: null,
              label: "No Selection",
              optionKey: "no-selection",
            };
            const optsList = isRequired ? baseOpts : [notReqOpt, ...baseOpts];
            const normalizedValue = formData?.[key];
            const value =
              normalizedValue === null || normalizedValue === undefined
                ? null
                : baseOpts.find((option) => String(option.id) === String(normalizedValue)) ?? null;
            const handleInputChange = enableSearchQuery
              ? (_, newValue, reason) => {
                  if (reason === "input") {
                    handleFieldSearchChange(key, newValue);
                  }
                  if (reason === "clear") {
                    handleFieldSearchChange(key, "");
                  }
                }
              : undefined;
            const inputValue = enableSearchQuery
              ? (fieldInputValues[key] ?? value?.label ?? "")
              : undefined;

            return (
              <div key={key} className="w-full">
                <CustomInputLabel text={label} isRequired={isRequired} />
                <Autocomplete
                  disabled={disableForm && initialFormData[key] !== null}
                  disableClearable={isRequired}
                  filterOptions={(_, state) => filterOptionsByLabel(optsList, state)}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.id) === String(value?.id) || null
                  }
                  inputValue={inputValue}
                  onInputChange={handleInputChange}
                  onChange={(_, val) => handleChange(key, val?.id ?? null)}
                  options={optsList}
                  renderOption={(props, option) => {
                    const keyProp = option.optionKey ?? option.id ?? option.label;
                    return (
                      <li {...props} key={keyProp}>
                        {option.label}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField {...params} size="small" sx={textFieldStyles} />
                  )}
                  size="small"
                  value={value}
                />
              </div>
            );
          })}
        </div>

        {/* Drag and Drop Box */}
        <div className="mt-8 w-full" data-testid="container-dragAndDropBox">
          <DragAndDropContainer
            initValue={formData?.fileName ? { name: formData.fileName } : formData?.fileName} // Necessary to set/clear the value on the DragAndDropContainer
            onValueChange={(_, val) => handleFileChange(val)}
          />
        </div>

        {/* Action Buttons */}
        <div className="mt-8 grid w-full grid-cols-2 gap-4" data-testid="container-uploadButton">
          <Button
            className="h-[40px] rounded-full bg-white text-center text-sm normal-case transition"
            disableElevation
            onClick={resetForm}
            variant="outlined"
            sx={clearButtonStyles}
          >
            Clear
          </Button>

          <Button
            className="h-[40px] w-full rounded-full text-sm normal-case transition"
            disabled={!allowSave || isLoading || !hasChanged}
            disableElevation
            onClick={handleSubmit}
            type="button"
            variant="contained"
            sx={uploadButtonStyles}
          >
            Save
          </Button>
        </div>
        <LoadingIndicator isLoading={isLoading} />
      </div>

      <ResponseModal
        description="The image/document was successfully updated"
        handleCloseFunc={uploadSuccess ? handleUploadSuccess : undefined}
        errorMessage={uploadFailed ? errorArray : ""}
        isError={uploadFailed}
        openBool={uploadSuccess || uploadFailed}
        setOpenBool={uploadFailed ? setUploadFailed : setUploadSuccess}
        title={`Update ${uploadFailed ? "Failed" : "Success"}`}
      />
    </Modal>
  );
};

export default memo(EditUploadDocument);
